/**
 * スタンプの画面（issue #260・#111・#492／docs/01 7-7）。
 *
 * ここでできること:
 *  - スタンプの**セット**（アプリの一覧の上のタブ）を作る・名前を変える・並べ替える・空のセットを消す（issue #492）
 *  - セットごとに、いまのスタンプの一覧を見る（画像つき・アプリに出ているか／取り下げ中か）
 *  - スタンプを**取り込む**（画像を選び、名前・入れるセット・並び順を決める。その場で新しいセットも作れる）
 *    → アプリのスタンプの盤にそのまま並ぶ
 *  - 名前・並び順・セットを**直す**／印を付けたスタンプを**まとめて別のセットへ移す**
 *  - **取り下げる**（盤に出なくなる。すでに送られたスタンプはそのまま残る）／**元に戻す**
 *
 * 決定22 に沿って、保存は明示のボタンで行い、終わったら必ず結果を出します。
 * 取り下げ・セットを消すは確認の窓をはさみます。
 * どの操作も操作履歴に残ります（サーバ側で記録）。
 *
 * 取り込みの手順は3つです（画像はサーバを通さず、置き場へ直接置きます）:
 *  1. 置き場へ書き込むための短い期限のリンクをもらう（`POST /admin/stickers/uploads`）
 *  2. そのリンクへ画像を置く
 *  3. 一覧に1件足す（`POST /admin/stickers`）
 * 3 で断られた（名前が長すぎる等）ときは、直して押し直すと**置いた画像をそのまま使い**、
 * 同じ画像を何度も置き直しません。新しいセットを作って取り込むときも、セットは1回だけ作ります。
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import type {
  AdminSticker,
  AdminStickerList,
  AdminStickerSet,
  StickerUploadTarget,
} from '../api/types.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { FilePicker } from '../components/ui/FilePicker.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { endsWithRequest, withReason } from '../format/sentence.js';

/** 受け付ける画像の形式（サーバの STICKER_IMAGE_TYPES と同じ）。 */
const IMAGE_TYPES = ['image/png', 'image/webp', 'image/jpeg'];
/** 画像1つの大きさの上限（サーバの STICKER_IMAGE_MAX_BYTES と同じ 1MB）。 */
const IMAGE_MAX_BYTES = 1024 * 1024;
/** 名前の長さの上限（サーバの STICKER_LABEL_MAX_LENGTH と同じ）。 */
const LABEL_MAX_LENGTH = 32;
/** セットの名前の長さの上限（サーバの STICKER_SET_NAME_MAX_LENGTH と同じ）。 */
const SET_NAME_MAX_LENGTH = 20;
/** 合い言葉の形（サーバと同じ。英小文字で始まり、英小文字・数字・下線だけ） */
const KEY_PATTERN = /^[a-z][a-z0-9_]{0,31}$/;
/** 取り込みの「セット」の選択肢のうち、「新しいセットを作る」を表す値（セットの ID と重ならない） */
const NEW_SET = 'new';
/** 一度にまとめて移せるスタンプの数（サーバの STICKER_MOVE_MAX と同じ・issue #508）。 */
const MOVE_MAX = 200;
/** 並び順の上限（サーバの SORT_ORDER_MAX と同じ）。 */
const SORT_ORDER_MAX = 9999;
/** 並び順を空のまま取り込もうとしたが、一番うしろがもう上限で自動では決められないとき。 */
const SORT_ORDER_FULL_MESSAGE =
  '自動で最後に並べられません。「並び順」に 0 から 9999 の数を入れてください。小さい数ほど前に並びます。';

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: string;
}

/** 置き終えた画像（3 で断られたときに、置き直さずに使い回すため）。 */
interface PlacedImage {
  file: File;
  uploadId: string;
  contentType: string;
}

/** 合い言葉の既定値（送った投稿の中でスタンプを指す名前。ふだんはそのままで構いません）。 */
function suggestKey(): string {
  const letters = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return `stamp_${Array.from(bytes, (b) => letters[b % letters.length]).join('')}`;
}

export function StickersPage(): ReactNode {
  const api = useApi();
  const [list, setList] = useState<AdminStickerList | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState<Banner | null>(null);
  const [busy, setBusy] = useState(false);
  /** 取り込みの最中か（「取り込む」のボタンだけに「取り込んでいます…」を出すため） */
  const [importing, setImporting] = useState(false);

  /** 一覧で見ているセット（まだ決めていなければ先頭のセット） */
  const [viewSetId, setViewSetId] = useState<string | null>(null);
  /** まとめて移すために印を付けたスタンプ */
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  /** まとめて移す先 */
  const [moveTarget, setMoveTarget] = useState('');

  // セットを作る・名前を変える・消す
  const [newSetName, setNewSetName] = useState('');
  const [renamingSet, setRenamingSet] = useState<AdminStickerSet | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingSet, setDeletingSet] = useState<AdminStickerSet | null>(null);

  // 取り込み
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  /** 入れるセット（空＝いま一覧で見ているセット・NEW_SET＝新しく作る） */
  const [importSet, setImportSet] = useState('');
  const [importNewSetName, setImportNewSetName] = useState('');
  const [key, setKey] = useState(suggestKey);
  /**
   * 識別名の欄を出しているか。ふだんは自動で入ったままで構わない欄なので、既定では
   * 「くわしい設定」の中にしまっておく（LINE の管理画面に無い項目・issue #275 の3）。
   * 識別名で断られたときは開いて、直す場所をお見せする
   */
  const [keyOpen, setKeyOpen] = useState(false);
  /** 一覧の画像が読めなくて読み直した時刻（画像のリンクは短い期限つき・issue #275 の2） */
  const imageReloadedAt = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const placed = useRef<PlacedImage | null>(null);

  // 直す（1件ずつ）
  const [editing, setEditing] = useState<AdminSticker | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editSortOrder, setEditSortOrder] = useState('');
  const [editSetId, setEditSetId] = useState('');

  // 取り下げの確認
  const [withdrawing, setWithdrawing] = useState<AdminSticker | null>(null);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  /**
   * 一覧の画像が出なかったとき。画像のリンクは5分ほどで切れるので、画面を開いたまま
   * 時間がたつと画像が出なくなる。そのときは一覧を読み直して新しいリンクに差し替える。
   * 画像そのものが壊れているときに読み直しを繰り返さないよう、1分に1回までにする
   */
  const onImageError = useCallback(() => {
    const now = Date.now();
    if (now - imageReloadedAt.current < 60_000) return;
    imageReloadedAt.current = now;
    reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .get<AdminStickerList>('/admin/stickers')
      .then((loaded) => {
        if (!cancelled) setList({ stickers: loaded.stickers, sets: loaded.sets ?? [] });
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  const sets = list?.sets ?? [];
  /** 一覧で見ているセット（消えていたら先頭へ） */
  const viewSet = sets.find((set) => set.id === viewSetId) ?? sets[0] ?? null;
  const viewStickers =
    list && viewSet ? list.stickers.filter((sticker) => sticker.set_id === viewSet.id) : [];
  /** 取り込む先（空なら見ているセット） */
  const importSetId = importSet === '' ? (viewSet?.id ?? '') : importSet;

  /** セットの先頭の、アプリに出ているスタンプ（アプリのタブに出る絵と同じ）。 */
  function coverOf(set: AdminStickerSet): AdminSticker | undefined {
    return list?.stickers.find((sticker) => sticker.set_id === set.id && sticker.enabled);
  }

  /**
   * そのセットの一番うしろの次の並び順（空なら 0）。
   * 取り下げ中のスタンプは数えません（アプリに出ているものの一番うしろ・issue #498）。
   * 一番うしろがもう上限（9999）のときは決められないので null（空欄にして、入れていただく）。
   */
  function nextSortOrderIn(setId: string): number | null {
    const orders = (list?.stickers ?? [])
      .filter((sticker) => sticker.set_id === setId && sticker.enabled)
      .map((sticker) => sticker.sort_order);
    if (orders.length === 0) return 0;
    const next = Math.max(...orders) + 1;
    return next > SORT_ORDER_MAX ? null : next;
  }
  /** 取り込みの並び順の欄に、空のままのときに使う数（新しいセットなら 0）。 */
  const importNextSortOrder = importSetId === NEW_SET ? 0 : nextSortOrderIn(importSetId);

  function setName(setId: string): string {
    return sets.find((set) => set.id === setId)?.name ?? '';
  }

  function failed(action: string, caught: unknown, retry: string): Banner {
    const reason = toMessage(caught);
    return {
      tone: 'error',
      message: withReason(action, reason),
      // 理由の文が次の一手まで言っているとき（通信の失敗の「…ご確認ください。」など）は足さない（#286 L-6）
      ...(endsWithRequest(reason) ? {} : { nextStep: retry }),
    };
  }

  function showSet(setId: string): void {
    setViewSetId(setId);
    setSelected(new Set());
    setMoveTarget('');
  }

  // ---------------------------------------------------------------------------------------
  // セット

  async function createSet(): Promise<void> {
    const name = newSetName.trim();
    const problem = checkSetName(name);
    if (problem !== null) {
      setResult({ tone: 'error', message: problem });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const created = await api.post<AdminStickerSet>('/admin/sticker-sets', { body: { name } });
      setNewSetName('');
      showSet(created.id);
      setResult({
        tone: 'success',
        message: `「${created.name}」のセットを作りました。`,
        nextStep:
          'スタンプを取り込むときに、このセットを選んでください。ほかのセットのスタンプを移すこともできます。',
      });
      reload();
    } catch (caught: unknown) {
      setResult(
        failed(
          'セットを作れませんでした',
          caught,
          '名前をご確認のうえ、もう一度「セットを作る」を押してください。',
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  async function saveRename(): Promise<void> {
    if (!renamingSet) return;
    const name = renameValue.trim();
    const problem = checkSetName(name);
    if (problem !== null) {
      setResult({ tone: 'error', message: problem });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.patch<AdminStickerSet>(`/admin/sticker-sets/${renamingSet.id}`, {
        body: { name },
      });
      setRenamingSet(null);
      setResult({ tone: 'success', message: `セットの名前を「${saved.name}」にしました。` });
      reload();
    } catch (caught: unknown) {
      setResult(
        failed(
          '名前を変えられませんでした',
          caught,
          '名前をご確認のうえ、もう一度「保存する」を押してください。',
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  /** セットを1つ上（アプリでは左）か下（右）へ動かす。 */
  async function moveSet(set: AdminStickerSet, direction: -1 | 1): Promise<void> {
    const order = sets.map((s) => s.id);
    const from = order.indexOf(set.id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= order.length) return;
    [order[from], order[to]] = [order[to]!, order[from]!];
    setBusy(true);
    setResult(null);
    try {
      await api.post<{ sets: AdminStickerSet[] }>('/admin/sticker-sets/order', {
        body: { set_ids: order },
      });
      setResult({
        tone: 'success',
        message: `「${set.name}」を1つ${direction < 0 ? '上' : '下'}へ動かしました。アプリのタブもこの順に並びます。`,
      });
      reload();
    } catch (caught: unknown) {
      setResult(
        failed('並べ替えられませんでした', caught, '画面を開き直して、もう一度お試しください。'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function deleteSet(set: AdminStickerSet): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      await api.del(`/admin/sticker-sets/${set.id}`);
      setDeletingSet(null);
      if (viewSetId === set.id) setViewSetId(null);
      setResult({ tone: 'success', message: `「${set.name}」のセットを消しました。` });
      reload();
    } catch (caught: unknown) {
      setDeletingSet(null);
      setResult(
        failed('セットを消せませんでした', caught, '画面を開き直して、もう一度お試しください。'),
      );
    } finally {
      setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------------------
  // まとめて移す

  async function moveSelected(): Promise<void> {
    const ids = viewStickers.filter((s) => selected.has(s.id)).map((s) => s.id);
    if (ids.length === 0) {
      setResult({ tone: 'error', message: '移すスタンプに印を付けてください。' });
      return;
    }
    if (ids.length > MOVE_MAX) {
      setResult({
        tone: 'error',
        message: `一度に移せるスタンプは${MOVE_MAX}個までです。`,
        nextStep: '印を減らしてから、もう一度お試しください。',
      });
      return;
    }
    if (moveTarget === '') {
      setResult({ tone: 'error', message: '移す先のセットを選んでください。' });
      return;
    }
    const target = setName(moveTarget);
    setBusy(true);
    setResult(null);
    try {
      const moved = await api.post<{ moved: number }>('/admin/stickers/move', {
        body: { sticker_ids: ids, set_id: moveTarget },
      });
      setSelected(new Set());
      setResult({
        tone: 'success',
        message: `${moved.moved}個のスタンプを「${target}」へ移しました。`,
        nextStep: `移したスタンプは「${target}」の一番うしろに並んでいます。`,
      });
      reload();
    } catch (caught: unknown) {
      setResult(failed('移せませんでした', caught, '画面を開き直して、もう一度お試しください。'));
    } finally {
      setBusy(false);
    }
  }

  function toggle(stickerId: string, on: boolean): void {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(stickerId);
      else next.delete(stickerId);
      return next;
    });
  }

  // ---------------------------------------------------------------------------------------
  // 取り込み

  /** 取り込む前に、画面の中で分かる間違いを先にお伝えする（置き場へ無駄に置かない）。 */
  function checkBeforeImport(): string | null {
    if (!file) return '取り込む画像を選んでください。';
    if (!IMAGE_TYPES.includes(file.type)) {
      return '画像は PNG・WebP・JPEG のどれかでお願いします。';
    }
    if (file.size > IMAGE_MAX_BYTES) {
      return '画像が大きすぎます（1MBまで）。小さくしてから、もう一度お選びください。';
    }
    if (label.trim() === '') return 'スタンプの名前を入れてください。';
    if ([...label.trim()].length > LABEL_MAX_LENGTH) {
      return `スタンプの名前は${LABEL_MAX_LENGTH}文字までです。`;
    }
    if (importSetId === NEW_SET) {
      const problem = checkSetName(importNewSetName.trim());
      if (problem !== null) return problem;
    }
    if (sortOrder.trim() !== '' && !isSortOrder(sortOrder)) {
      return '並び順は 0 から 9999 の数でお願いします。';
    }
    if (sortOrder.trim() === '' && importNextSortOrder === null) {
      return SORT_ORDER_FULL_MESSAGE;
    }
    if (!KEY_PATTERN.test(key.trim())) {
      return '識別名は、英小文字で始まり、英小文字・数字・下線（_）だけでお願いします。';
    }
    return null;
  }

  async function importSticker(): Promise<void> {
    const problem = checkBeforeImport();
    if (problem !== null && problem.startsWith('識別名')) setKeyOpen(true);
    if (problem !== null || !file) {
      setResult({ tone: 'error', message: problem ?? '取り込む画像を選んでください。' });
      return;
    }
    setBusy(true);
    setImporting(true);
    setResult(null);
    try {
      // 新しいセットを作ってから入れる（作れたら選択をそのセットに替え、押し直しても2つ作らない）
      let setId = importSetId;
      if (setId === NEW_SET) {
        const created = await api.post<AdminStickerSet>('/admin/sticker-sets', {
          body: { name: importNewSetName.trim() },
        });
        setId = created.id;
        setImportSet(created.id);
        setImportNewSetName('');
        reload();
      }
      // 1・2: 画像を置く（前の回に同じ画像を置き終えていれば、それを使う）
      let image = placed.current;
      if (image === null || image.file !== file) {
        const target = await api.post<StickerUploadTarget>('/admin/stickers/uploads', {
          body: { content_type: file.type, size_bytes: file.size },
        });
        await api.putFile(target.upload_url, file, target.upload_headers);
        image = { file, uploadId: target.upload_id, contentType: file.type };
        placed.current = image;
      }
      // 3: 一覧に1件足す（セットがまだ1つも無ければ、サーバが「標準」を作って入れる）
      const created = await api.post<AdminSticker>('/admin/stickers', {
        body: {
          key: key.trim(),
          label: label.trim(),
          upload_id: image.uploadId,
          content_type: image.contentType,
          ...(setId === '' ? {} : { set_id: setId }),
          // 空のままなら一番うしろ（上限で決められないときは、取り込む前の確かめで止めています）
          sort_order: sortOrder.trim() === '' ? (nextSortOrderIn(setId) ?? 0) : Number(sortOrder),
        },
      });
      placed.current = null;
      setFile(null);
      setLabel('');
      setSortOrder('');
      setKey(suggestKey());
      if (fileInput.current) fileInput.current.value = '';
      // 取り込んだセットを一覧に出す（続けて同じセットへ取り込める）
      setImportSet('');
      showSet(created.set_id);
      setResult({
        tone: 'success',
        message: `「${created.label}」を取り込みました。アプリのスタンプ一覧に並びます。`,
        nextStep: 'アプリでスタンプ一覧を開き直すと、新しいスタンプが出ます。',
      });
      reload();
    } catch (caught: unknown) {
      // 同じ識別名がすでにある（409）: しまってある識別名の欄を開いて、直す場所をお見せする
      if (caught instanceof ApiError && caught.status === 409) setKeyOpen(true);
      setResult(
        failed(
          '取り込めませんでした',
          caught,
          '入力の内容をご確認のうえ、もう一度「取り込む」を押してください。',
        ),
      );
    } finally {
      setBusy(false);
      setImporting(false);
    }
  }

  // ---------------------------------------------------------------------------------------
  // 1件ずつ直す・取り下げる

  function startEditing(sticker: AdminSticker): void {
    setEditing(sticker);
    setEditLabel(sticker.label);
    setEditSortOrder(String(sticker.sort_order));
    setEditSetId(sticker.set_id);
    setResult(null);
  }

  async function saveEdit(): Promise<void> {
    if (!editing) return;
    const nextLabel = editLabel.trim();
    if (nextLabel === '' || [...nextLabel].length > LABEL_MAX_LENGTH) {
      setResult({
        tone: 'error',
        message: `スタンプの名前は1文字以上、${LABEL_MAX_LENGTH}文字までで入れてください。`,
      });
      return;
    }
    if (!isSortOrder(editSortOrder)) {
      setResult({ tone: 'error', message: '並び順は 0 から 9999 の数でお願いします。' });
      return;
    }
    const moving = editSetId !== editing.set_id;
    // セットを移すときに並び順を触っていなければ、移した先の一番うしろ（サーバが決める）
    const keepOrder = moving && Number(editSortOrder) === editing.sort_order;
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.patch<AdminSticker>(`/admin/stickers/${editing.id}`, {
        body: {
          label: nextLabel,
          ...(keepOrder ? {} : { sort_order: Number(editSortOrder) }),
          ...(moving ? { set_id: editSetId } : {}),
        },
      });
      setEditing(null);
      setResult({
        tone: 'success',
        message: moving
          ? `「${saved.label}」を保存し、「${setName(editSetId)}」のセットへ移しました。`
          : `「${saved.label}」の名前と並び順を保存しました。`,
      });
      reload();
    } catch (caught: unknown) {
      setResult(
        failed(
          '保存できませんでした',
          caught,
          '入力の内容をご確認のうえ、もう一度「保存する」を押してください。',
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  async function setEnabled(sticker: AdminSticker, enabled: boolean): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      await api.patch<AdminSticker>(`/admin/stickers/${sticker.id}`, { body: { enabled } });
      setWithdrawing(null);
      setResult(
        enabled
          ? {
              tone: 'success',
              message: `「${sticker.label}」を元に戻しました。アプリのスタンプ一覧にまた並びます。`,
            }
          : {
              tone: 'success',
              message: `「${sticker.label}」を取り下げました。アプリのスタンプ一覧に出なくなります。`,
              nextStep:
                'すでに送られたスタンプは、そのまま残ります。「元に戻す」でいつでも戻せます。',
            },
      );
      reload();
    } catch (caught: unknown) {
      setWithdrawing(null);
      setResult({
        tone: 'error',
        message: withReason(
          `${enabled ? '元に戻せませんでした' : '取り下げられませんでした'}`,
          toMessage(caught),
        ),
        nextStep: '時間をおいて、もう一度お試しください。',
      });
    } finally {
      setBusy(false);
    }
  }

  const chosen = viewStickers.filter((sticker) => selected.has(sticker.id));
  const moveTargets = sets.filter((set) => set.id !== viewSet?.id);

  return (
    <article>
      <PageHeader
        title="スタンプ"
        description="アプリのスタンプ一覧に並ぶスタンプを、セット（種類）ごとに取り込み、並び順を決めます。取り下げたスタンプは一覧に出なくなりますが、すでに送られたものはそのまま残ります。"
      />

      {result ? (
        <ResultBanner
          reveal
          tone={result.tone}
          message={result.message}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
        />
      ) : null}

      {error ? <ErrorBlock failure={error} onRetry={reload} /> : null}
      {!error && !list ? <LoadingBlock label="スタンプの一覧を読み込んでいます…" /> : null}

      {list ? (
        <Card
          title="セット"
          description="アプリのスタンプ一覧の上に、この順でタブが並びます（上にあるセットほど左）。タブには、そのセットの先頭のスタンプの絵が出ます。セットが1つだけのときは、タブは出ません。"
        >
          {sets.length === 0 ? (
            <EmptyBlock label="セットはまだありません。スタンプを取り込むと「標準」のセットができます。先に下でセットを作ることもできます。" />
          ) : (
            <DataTable label="セットの一覧">
              <thead>
                <tr>
                  <th scope="col">タブの絵</th>
                  <th scope="col">名前</th>
                  <th scope="col">スタンプの数</th>
                  <th scope="col">並び</th>
                  <th scope="col">
                    <span className="admin-visually-hidden">操作</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sets.map((set, index) => {
                  const cover = coverOf(set);
                  const empty = set.enabled_count + set.retired_count === 0;
                  return (
                    <tr key={set.id}>
                      <td>
                        {cover ? (
                          <img
                            className="admin-sticker-thumb is-small"
                            src={cover.url}
                            alt={`${set.name}のタブの絵（${cover.label}）`}
                            width={40}
                            height={40}
                            onError={onImageError}
                          />
                        ) : (
                          <span className="ui-cell-soft">絵なし</span>
                        )}
                      </td>
                      <th scope="row">{set.name}</th>
                      <td>
                        {set.enabled_count}個
                        {set.retired_count > 0 ? `（ほかに取り下げ中 ${set.retired_count}個）` : ''}
                        {set.enabled_count === 0 ? (
                          <span className="admin-set-note">アプリにタブは出ません</span>
                        ) : null}
                      </td>
                      <td>
                        <div className="admin-row-actions">
                          <Button
                            variant="quiet"
                            compact
                            disabled={busy || index === 0}
                            aria-label={`「${set.name}」を1つ上へ`}
                            onClick={() => void moveSet(set, -1)}
                          >
                            ↑ 上へ
                          </Button>
                          <Button
                            variant="quiet"
                            compact
                            disabled={busy || index === sets.length - 1}
                            aria-label={`「${set.name}」を1つ下へ`}
                            onClick={() => void moveSet(set, 1)}
                          >
                            ↓ 下へ
                          </Button>
                        </div>
                      </td>
                      <td>
                        <div className="admin-row-actions">
                          <Button
                            variant="secondary"
                            compact
                            disabled={busy}
                            onClick={() => showSet(set.id)}
                            aria-label={`「${set.name}」のスタンプを見る`}
                          >
                            スタンプを見る
                          </Button>
                          <Button
                            variant="secondary"
                            compact
                            disabled={busy}
                            onClick={() => {
                              setRenamingSet(set);
                              setRenameValue(set.name);
                              setResult(null);
                            }}
                            aria-label={`「${set.name}」の名前を変える`}
                          >
                            名前を変える
                          </Button>
                          {empty ? (
                            <Button
                              variant="quiet"
                              compact
                              disabled={busy}
                              onClick={() => setDeletingSet(set)}
                              aria-label={`「${set.name}」のセットを消す`}
                            >
                              消す
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
          )}

          {renamingSet ? (
            <FormCard
              bare
              title={`「${renamingSet.name}」の名前を変える`}
              submitLabel="保存する"
              submitBusyLabel="保存しています…"
              busy={busy}
              onSubmit={() => void saveRename()}
              onCancel={() => setRenamingSet(null)}
            >
              <label className="ui-field" htmlFor="sticker-set-rename">
                <span>セットの名前（{SET_NAME_MAX_LENGTH}文字まで）</span>
                <input
                  id="sticker-set-rename"
                  type="text"
                  value={renameValue}
                  onChange={(event) => setRenameValue(event.target.value)}
                />
              </label>
            </FormCard>
          ) : null}

          {error?.status === 403 ? null : (
            <FormCard
              bare
              title="新しいセットを作る"
              submitLabel="セットを作る"
              submitBusyLabel="作っています…"
              submitVariant="secondary"
              busy={busy && renamingSet === null && !importing}
              onSubmit={() => void createSet()}
            >
              <label className="ui-field" htmlFor="sticker-set-new">
                <span>新しいセットの名前（{SET_NAME_MAX_LENGTH}文字まで。例：あいさつ、野球）</span>
                <input
                  id="sticker-set-new"
                  type="text"
                  value={newSetName}
                  onChange={(event) => setNewSetName(event.target.value)}
                />
              </label>
            </FormCard>
          )}
        </Card>
      ) : null}

      {list ? (
        <Card
          title="いまのスタンプ"
          description="セットを選ぶと、そのセットのスタンプが出ます。並び順の小さいものから、アプリの一覧の左上に並びます。"
        >
          {list.stickers.length === 0 && sets.length === 0 ? (
            <EmptyBlock label="スタンプはまだありません。下の「スタンプを取り込む」から足してください。" />
          ) : (
            <>
              {sets.length > 1 ? (
                <div className="admin-tabs" role="group" aria-label="見るセットの切り替え">
                  {sets.map((set) => (
                    <button
                      key={set.id}
                      type="button"
                      className="admin-tab"
                      aria-pressed={viewSet?.id === set.id}
                      onClick={() => showSet(set.id)}
                    >
                      {set.name}（{set.enabled_count}）
                    </button>
                  ))}
                </div>
              ) : null}
              {viewStickers.length === 0 ? (
                <EmptyBlock
                  label={`「${viewSet?.name ?? ''}」には、まだスタンプがありません。下の「スタンプを取り込む」でこのセットを選んで取り込むか、ほかのセットから移してください。`}
                />
              ) : (
                <>
                  {moveTargets.length > 0 ? (
                    <div
                      className="admin-bulk-bar"
                      role="group"
                      aria-label="まとめて別のセットへ移す"
                    >
                      <span className="admin-bulk-count" aria-live="polite">
                        {chosen.length === 0
                          ? '別のセットへ移すときは、スタンプに印を付けてください。'
                          : chosen.length > MOVE_MAX
                            ? `${chosen.length}個を選んでいます。一度に移せるのは${MOVE_MAX}個までです。印を減らしてください。`
                            : viewStickers.length > MOVE_MAX
                              ? `${chosen.length}個を選んでいます。一度に移せるのは${MOVE_MAX}個までです。`
                              : `${chosen.length}個を選んでいます。`}
                      </span>
                      {/* 一度に移せる数より多いセットでは、上から移せる数だけ選ぶ（issue #508） */}
                      <Button
                        variant="secondary"
                        compact
                        disabled={busy}
                        onClick={() =>
                          setSelected(new Set(viewStickers.slice(0, MOVE_MAX).map((s) => s.id)))
                        }
                      >
                        {viewStickers.length > MOVE_MAX
                          ? `上から${MOVE_MAX}個を選ぶ`
                          : 'すべて選ぶ'}
                      </Button>
                      <Button
                        variant="quiet"
                        compact
                        disabled={busy || chosen.length === 0}
                        onClick={() => setSelected(new Set())}
                      >
                        選ぶのをやめる
                      </Button>
                      <label className="ui-field admin-move-target" htmlFor="sticker-move-target">
                        <span>移す先</span>
                        <select
                          id="sticker-move-target"
                          value={moveTarget}
                          onChange={(event) => setMoveTarget(event.target.value)}
                        >
                          <option value="">選んでください</option>
                          {moveTargets.map((set) => (
                            <option key={set.id} value={set.id}>
                              {set.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <Button
                        variant="secondary"
                        busy={busy && chosen.length > 0 && moveTarget !== ''}
                        busyLabel="移しています…"
                        disabled={busy || chosen.length === 0 || chosen.length > MOVE_MAX}
                        onClick={() => void moveSelected()}
                      >
                        選んだスタンプを移す
                      </Button>
                    </div>
                  ) : null}
                  <DataTable label="スタンプの一覧">
                    <thead>
                      <tr>
                        {moveTargets.length > 0 ? <th scope="col">選ぶ</th> : null}
                        <th scope="col">画像</th>
                        <th scope="col">名前</th>
                        <th scope="col">並び順</th>
                        <th scope="col">状態</th>
                        <th scope="col">
                          <span className="admin-visually-hidden">操作</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewStickers.map((sticker) => (
                        <tr key={sticker.id}>
                          {moveTargets.length > 0 ? (
                            <td>
                              <label className="ui-check" htmlFor={`sticker-pick-${sticker.id}`}>
                                <input
                                  id={`sticker-pick-${sticker.id}`}
                                  type="checkbox"
                                  checked={selected.has(sticker.id)}
                                  onChange={(event) => toggle(sticker.id, event.target.checked)}
                                />
                                <span className="admin-visually-hidden">
                                  「{sticker.label}」を選ぶ
                                </span>
                              </label>
                            </td>
                          ) : null}
                          <td>
                            <img
                              className="admin-sticker-thumb"
                              src={sticker.url}
                              alt={`${sticker.label}のスタンプ`}
                              width={56}
                              height={56}
                              onError={onImageError}
                            />
                          </td>
                          <th scope="row">{sticker.label}</th>
                          <td>{sticker.sort_order}</td>
                          <td>
                            {sticker.enabled ? (
                              <span className="admin-mark-yes">✓ アプリに出ています</span>
                            ) : (
                              <span className="admin-badge">取り下げ中（アプリに出ません）</span>
                            )}
                          </td>
                          <td>
                            <div className="admin-row-actions">
                              <Button
                                variant="secondary"
                                compact
                                disabled={busy}
                                onClick={() => startEditing(sticker)}
                              >
                                名前・並び順・セットを直す
                              </Button>
                              {sticker.enabled ? (
                                <Button
                                  variant="quiet"
                                  compact
                                  disabled={busy}
                                  onClick={() => setWithdrawing(sticker)}
                                >
                                  取り下げる
                                </Button>
                              ) : (
                                <Button
                                  variant="secondary"
                                  compact
                                  disabled={busy}
                                  onClick={() => void setEnabled(sticker, true)}
                                >
                                  元に戻す
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </>
              )}
            </>
          )}
        </Card>
      ) : null}

      {editing ? (
        <FormCard
          title={`「${editing.label}」を直す`}
          submitLabel="保存する"
          submitBusyLabel="保存しています…"
          busy={busy}
          onSubmit={() => void saveEdit()}
          onCancel={() => setEditing(null)}
        >
          <label className="ui-field" htmlFor="sticker-edit-label">
            <span>名前（{LABEL_MAX_LENGTH}文字まで）</span>
            <input
              id="sticker-edit-label"
              type="text"
              value={editLabel}
              onChange={(event) => setEditLabel(event.target.value)}
            />
          </label>
          <label className="ui-field" htmlFor="sticker-edit-set">
            <span>セット（変えると、そのセットの一番うしろに移ります）</span>
            <select
              id="sticker-edit-set"
              value={editSetId}
              onChange={(event) => setEditSetId(event.target.value)}
            >
              {sets.map((set) => (
                <option key={set.id} value={set.id}>
                  {set.name}
                </option>
              ))}
            </select>
          </label>
          <label className="ui-field" htmlFor="sticker-edit-sort">
            <span>並び順（0〜9999。小さいほど前に並びます）</span>
            <input
              id="sticker-edit-sort"
              type="number"
              inputMode="numeric"
              min={0}
              max={9999}
              value={editSortOrder}
              onChange={(event) => setEditSortOrder(event.target.value)}
            />
          </label>
        </FormCard>
      ) : null}

      {error?.status === 403 ? null : (
        <FormCard
          title="スタンプを取り込む"
          description="画像は PNG・WebP・JPEG で、1MBまでです。取り込むと、アプリのスタンプ一覧にそのまま並びます。"
          submitLabel="取り込む"
          submitBusyLabel="取り込んでいます…"
          busy={importing}
          onSubmit={() => void importSticker()}
        >
          <FilePicker
            id="sticker-file"
            label="画像"
            accept={IMAGE_TYPES.join(',')}
            file={file}
            onChange={setFile}
            inputRef={fileInput}
          />
          <label className="ui-field" htmlFor="sticker-label">
            <span>名前（{LABEL_MAX_LENGTH}文字まで。例：了解、ありがとう）</span>
            <input
              id="sticker-label"
              type="text"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
          <label className="ui-field" htmlFor="sticker-set">
            <span>入れるセット</span>
            <select
              id="sticker-set"
              value={importSetId}
              onChange={(event) => setImportSet(event.target.value)}
            >
              {sets.length === 0 ? <option value="">標準（はじめてのセット）</option> : null}
              {sets.map((set) => (
                <option key={set.id} value={set.id}>
                  {set.name}
                </option>
              ))}
              <option value={NEW_SET}>新しいセットを作って入れる</option>
            </select>
          </label>
          {importSetId === NEW_SET ? (
            <label className="ui-field" htmlFor="sticker-set-name">
              <span>新しいセットの名前（{SET_NAME_MAX_LENGTH}文字まで）</span>
              <input
                id="sticker-set-name"
                type="text"
                value={importNewSetName}
                onChange={(event) => setImportNewSetName(event.target.value)}
              />
            </label>
          ) : null}
          <label className="ui-field" htmlFor="sticker-sort">
            <span>
              {importNextSortOrder === null
                ? '並び順（0〜9999。自動で最後に並べられないため、数を入れてください）'
                : '並び順（0〜9999。空のままなら、そのセットの一番うしろに並びます）'}
            </span>
            <input
              id="sticker-sort"
              type="number"
              inputMode="numeric"
              min={0}
              max={9999}
              placeholder={importNextSortOrder === null ? '' : String(importNextSortOrder)}
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
            />
          </label>
          <details
            className="admin-details"
            open={keyOpen}
            onToggle={(event) => setKeyOpen(event.currentTarget.open)}
          >
            <summary>くわしい設定</summary>
            <label className="ui-field" htmlFor="sticker-key">
              <span>識別名（英小文字。ふだんは変えなくて構いません）</span>
              <input
                id="sticker-key"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={key}
                onChange={(event) => setKey(event.target.value)}
              />
            </label>
          </details>
        </FormCard>
      )}

      <ConfirmDialog
        open={withdrawing !== null}
        title="このスタンプを取り下げます"
        message={
          withdrawing
            ? `「${withdrawing.label}」が、アプリのスタンプ一覧に出なくなります。すでに送られたスタンプは、そのまま残ります。`
            : ''
        }
        reversibility="「元に戻す」で、いつでも一覧に戻せます。取り下げた記録は操作履歴に残ります。"
        confirmLabel="取り下げる"
        busy={busy}
        onConfirm={() => {
          if (withdrawing) void setEnabled(withdrawing, false);
        }}
        onCancel={() => setWithdrawing(null)}
      />

      <ConfirmDialog
        open={deletingSet !== null}
        title="このセットを消します"
        message={
          deletingSet
            ? `「${deletingSet.name}」のセットを消します。スタンプは入っていないので、アプリの一覧は変わりません。`
            : ''
        }
        reversibility="消したセットは戻せません。同じ名前で作り直すことはできます。消した記録は操作履歴に残ります。"
        confirmLabel="消す"
        busy={busy}
        onConfirm={() => {
          if (deletingSet) void deleteSet(deletingSet);
        }}
        onCancel={() => setDeletingSet(null)}
      />
    </article>
  );
}

function isSortOrder(value: string): boolean {
  const n = Number(value);
  return value.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= SORT_ORDER_MAX;
}

/** セットの名前の、画面の中で分かる間違い（サーバと同じ決まり）。 */
function checkSetName(name: string): string | null {
  if (name === '') return 'セットの名前を入れてください。';
  if ([...name].length > SET_NAME_MAX_LENGTH) {
    return `セットの名前は${SET_NAME_MAX_LENGTH}文字までです。`;
  }
  return null;
}

/** 失敗の理由を、運用される方が読める言葉にする。 */
function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}
